import { WorksheetGridKeys } from 'Components/WorksheetGrids/WorksheetGridDefinitions';
import { RootState } from 'Data/Redux';
import { actions as worksheetActions } from 'Data/ReduxViewModels/WorksheetEditor';
import { actions as worksheetGridActions } from 'Data/ReduxViewModels/WorksheetGrid';
import { hasEmptyValue, hasNonEmptyValue, hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import deepEquals from 'Lib/Object/deepEquals';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { Observable, concat, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { actions as barcodeActions } from '.';
import * as oPath from '../../../Lib/Object/Path/Immutable';
import webApi from '../../../Lib/WebApi';
import { addErrorNotice, callApi, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import { WorkflowType } from './types';

export const postBarcode: Epic = (action$, state$) =>
  action$.pipe(
    ofType(barcodeActions.postBarcode.toString()),
    switchMap(() => {
      const state: RootState = state$.value;
      const {
        barcode: { barcode: BarcodeText, focusedRowUUID, focusedTareId },
        worksheetGrid: { gridType },
      } = state;

      var labUUID = state.worksheetGrid?.query?.params?.LaboratoryId ?? state.user?.data?.HomeLaboratory?.Id;

      return callApi(
        webApi.barcode.post({
          Body: {
            BarcodeText,
            LaboratoryUUID: labUUID,
          },
        })
      ).pipe(
        switchMap((response) => {
          const {
            Body: { ActiveWorkflow, ResponseMessage },
          } = response;

          if (ActiveWorkflow.WorkflowType === WorkflowType.MoistureTinRegistration) {
            //If we are on the Wet Mass Grid and have a row focused
            if (hasValue(focusedRowUUID) && gridType === WorksheetGridKeys.WetMassEntry) {
              const worksheets = state.worksheetGrid?.worksheets;
              if (
                hasNonEmptyValue(worksheets) &&
                hasEmptyValue(worksheets[focusedRowUUID!]?.syncModel?.data?.DryingTray.ContainerLabel)
              )
                return syncWorksheetGridAndAwaitResolve(
                  ActiveWorkflow.ResourceId,
                  focusedRowUUID!,
                  action$,
                  of(
                    barcodeActions.postBarcodeResolve({
                      ActiveWorkflow: { ...ActiveWorkflow, ResourceId: focusedRowUUID },
                    })
                  )
                );
            }

            const {
              barcode: { tareIdList },
              worksheetEditor: { syncModel: worksheet },
            } = state;

            //If we are on a Worksheet and have the cursor in a Tare ID field
            if (hasValue(focusedTareId)) {
              const moistureTinPath = tareIdList.filter((t: any) => t.id === focusedTareId)[0]?.path;

              if (hasValue(moistureTinPath) && hasEmptyValue(oPath.get(worksheet, moistureTinPath)))
                return syncAndAwaitResolve(
                  ActiveWorkflow.ResourceId,
                  moistureTinPath,
                  action$,
                  of(barcodeActions.postBarcodeResolve({ ActiveWorkflow }))
                );
            }

            //If we are on a worksheet that has drying trays
            if (hasValue(tareIdList)) {
              // If a tin is regsitered and then scanned again before the registration completes, it may attempt to
              // register again. To counteract this, we first look for a tareId that is matched with only the id of the
              // scanned tin. This will only be the case if a registration occurred but had yet to finish syncing.
              var existingAssociation = tareIdList.find((t: any) =>
                deepEquals(oPath.get(worksheet, t.path), { Id: ActiveWorkflow.ResourceId })
              );
              if (hasNonEmptyValue(existingAssociation)) {
                return of(
                  barcodeActions.postBarcodeResolve({
                    ActiveWorkflow: { ...ActiveWorkflow, ResourceId: existingAssociation?.id },
                  })
                );
              }

              for (var i = 0; i < tareIdList.length; i++) {
                // If the moisture tin or the tare ID have values, then move to the next field
                if (
                  hasEmptyValue(oPath.get(worksheet, tareIdList[i]?.path)) &&
                  hasEmptyValue(oPath.get(worksheet, tareIdList[i]?.value))
                ) {
                  return syncAndAwaitResolve(
                    ActiveWorkflow.ResourceId,
                    tareIdList[i]?.path,
                    action$,
                    of(
                      barcodeActions.postBarcodeResolve({
                        ActiveWorkflow: { ...ActiveWorkflow, ResourceId: tareIdList[i].id },
                      })
                    )
                  );
                }
              }
            }

            ActiveWorkflow.WorkflowType = WorkflowType.Invalid;
          }

          return of(barcodeActions.postBarcodeResolve({ ActiveWorkflow, ResponseMessage }));
        }),
        catchWithAuthErrors((err: any) =>
          of(barcodeActions.postBarcodeReject(), addErrorNotice('Failed to read barcode', err))
        )
      );
    })
  );

const syncWorksheetGridAndAwaitResolve = (
  moistureTinUUID: Guid,
  focusedRowUUID: Guid,
  action$: Observable<any>,
  resolve: Observable<Action>
) =>
  concat(
    // Emit forced sync action
    of(
      worksheetGridActions.setWorksheetValue({
        name: 'data.DryingTray.MoistureTin',
        value: { Id: moistureTinUUID },
        worksheetId: focusedRowUUID,
      })
    ),
    of(worksheetGridActions.synchroniseWorksheet({ worksheetId: focusedRowUUID, force: true })),
    // Listen for the syncResolve action, then resolve
    action$.pipe(
      ofType(worksheetGridActions.synchroniseWorksheetComplete.toString()),
      take(1),
      switchMap(() => resolve)
    )
  );

const syncAndAwaitResolve = (
  moistureTinUUID: Guid,
  path: string,
  action$: Observable<any>,
  resolve: Observable<Action>
) =>
  concat(
    // Emit forced sync action
    of(
      worksheetActions.setValue({
        name: path,
        value: { Id: moistureTinUUID },
        sync: true,
      })
    ),
    // Listen for the syncResolve action, then resolve
    action$.pipe(
      ofType(worksheetActions.syncResolve.toString()),
      take(1),
      switchMap(() => resolve)
    )
  );
